import { useState } from 'react'
import { Plus, Edit, Trash2, Eye, EyeOff, Music2, ChevronRight, Mic2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useMusicReleases, useDeleteRelease, useUpdateRelease, useCoverSongs, useDeleteCoverSong } from '@/hooks/use-music'
import type { Tables } from '@/types/supabase'
import ReleaseFormModal from '@/components/music/release-form-modal'
import CoverSongFormModal from '@/components/music/cover-song-form-modal'
import { FeatureGate } from '@/components/feature-gate'

type MusicRelease = Tables<'music_releases'>
type CoverSong = Tables<'cover_songs'>
type Tab = 'diskografi' | 'lagu-bawaan'

const RELEASE_TYPE_LABELS: Record<string, string> = {
  single: 'Single',
  album: 'Album',
  ep: 'EP',
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'diskografi', label: 'Diskografi' },
  { key: 'lagu-bawaan', label: 'Lagu Bawaan' },
]

export default function MusicPage() {
  const { data: releases = [], isLoading: loadingReleases } = useMusicReleases()
  const { data: coverSongs = [], isLoading: loadingCovers } = useCoverSongs()
  const deleteRelease = useDeleteRelease()
  const updateRelease = useUpdateRelease()
  const deleteCoverSong = useDeleteCoverSong()

  const [activeTab, setActiveTab] = useState<Tab>('diskografi')
  const [releaseModalOpen, setReleaseModalOpen] = useState(false)
  const [editingRelease, setEditingRelease] = useState<MusicRelease | undefined>(undefined)
  const [coverModalOpen, setCoverModalOpen] = useState(false)
  const [editingSong, setEditingSong] = useState<CoverSong | undefined>(undefined)

  function openCreateRelease() {
    setEditingRelease(undefined)
    setReleaseModalOpen(true)
  }

  function openEditRelease(release: MusicRelease) {
    setEditingRelease(release)
    setReleaseModalOpen(true)
  }

  function closeReleaseModal() {
    setReleaseModalOpen(false)
    setEditingRelease(undefined)
  }

  function openCreateCover() {
    setEditingSong(undefined)
    setCoverModalOpen(true)
  }

  function openEditCover(song: CoverSong) {
    setEditingSong(song)
    setCoverModalOpen(true)
  }

  function closeCoverModal() {
    setCoverModalOpen(false)
    setEditingSong(undefined)
  }

  const isLoading = activeTab === 'diskografi' ? loadingReleases : loadingCovers

  if (isLoading) return <p className="text-muted-foreground">Memuat...</p>

  return (
    <FeatureGate feature="music">
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Musik</h1>
          <p className="text-muted-foreground">Kelola discography dan lagu bawaan</p>
        </div>
        {activeTab === 'diskografi' ? (
          <button
            onClick={openCreateRelease}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Tambah Release
          </button>
        ) : (
          <button
            onClick={openCreateCover}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Tambah Lagu Bawaan
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-border">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab: Diskografi */}
      {activeTab === 'diskografi' && (
        releases.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center">
            <Music2 className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="font-medium">Belum ada release</p>
            <p className="text-sm text-muted-foreground mt-1">Tambahkan album atau single pertama Anda.</p>
            <button
              onClick={openCreateRelease}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Tambah Release
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {releases.map((release) => (
              <div
                key={release.id}
                className="group rounded-xl border border-border bg-card overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Cover art */}
                <Link to={`/music/${release.id}`} className="block aspect-square bg-muted relative overflow-hidden">
                  {release.cover_url ? (
                    <img
                      src={release.cover_url}
                      alt={release.title}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Music2 className="h-12 w-12 text-muted-foreground/30" />
                    </div>
                  )}
                  {/* Published badge */}
                  <span
                    className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                      release.is_published
                        ? 'bg-green-500/90 text-white'
                        : 'bg-black/50 text-white'
                    }`}
                  >
                    {release.is_published ? 'Publik' : 'Draft'}
                  </span>
                </Link>

                {/* Info */}
                <div className="p-3 space-y-1">
                  <Link
                    to={`/music/${release.id}`}
                    className="block font-medium text-sm leading-tight hover:text-primary transition-colors"
                  >
                    {release.title}
                  </Link>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{RELEASE_TYPE_LABELS[release.release_type] ?? release.release_type}</span>
                    {release.release_date && (
                      <>
                        <span>·</span>
                        <span>{release.release_date.slice(0, 4)}</span>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-1">
                    <Link
                      to={`/music/${release.id}`}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      Lihat tracks
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() =>
                          updateRelease.mutate({ id: release.id, is_published: !release.is_published })
                        }
                        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        title={release.is_published ? 'Sembunyikan' : 'Publikasikan'}
                      >
                        {release.is_published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => openEditRelease(release)}
                        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        title="Edit release"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Hapus "${release.title}"? Semua track akan ikut terhapus.`))
                            deleteRelease.mutate(release.id)
                        }}
                        className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        title="Hapus release"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Tab: Lagu Bawaan */}
      {activeTab === 'lagu-bawaan' && (
        coverSongs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center">
            <Mic2 className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="font-medium">Belum ada lagu bawaan</p>
            <p className="text-sm text-muted-foreground mt-1">Tambahkan lagu dari artis/grup lain yang sering dibawakan.</p>
            <button
              onClick={openCreateCover}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Tambah Lagu Bawaan
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Judul Lagu</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Artis / Grup Asli</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">Catatan</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {coverSongs.map((song, i) => (
                  <tr
                    key={song.id}
                    className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}
                  >
                    <td className="px-4 py-3 font-medium">{song.title}</td>
                    <td className="px-4 py-3 text-muted-foreground">{song.original_artist}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                      {song.notes ?? <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditCover(song)}
                          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          title="Edit"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Hapus "${song.title}"?`))
                              deleteCoverSong.mutate(song.id)
                          }}
                          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Hapus"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <ReleaseFormModal
        open={releaseModalOpen}
        onClose={closeReleaseModal}
        release={editingRelease}
      />
      <CoverSongFormModal
        open={coverModalOpen}
        onClose={closeCoverModal}
        song={editingSong}
      />
    </div>
    </FeatureGate>
  )
}
